import { t } from "@lingui/macro";
import {
  BudgetItemPlannedExpensesForCreateExpenseDocument,
  type BudgetItemPlannedExpensesForCreateExpenseQuery,
  type BudgetItemPlannedExpensesForCreateExpenseQueryVariables,
  ExpenseBudgetItemTypeEnum,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { NumberModel } from "@src/utils/models";
import { FieldState, FormState } from "formstate";
import { uniqueId } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { TAddExpenseItemArgs } from "../../../models";
import type { BudgetItemStore } from "../BudgetItemStore";

type PlannedExpenseMappingModalData = {
  onSave: (
    usages: NonNullable<TAddExpenseItemArgs["planned_expense_usages"]>,
  ) => void;
};

type PlannedExpenseMappingRowSrc =
  BudgetItemPlannedExpensesForCreateExpenseQuery["budgetItemPlannedExpenses"][0];

type PlannedExpenseMappingRow = {
  id: string;
  mappedAmount: NumberModel;
};

class PlannedExpenseMappingRowForm extends FormState<
  TToFieldStates<PlannedExpenseMappingRow>
> {
  readonly key: string;
  readonly description?: string;
  readonly contractorId: string;
  readonly date?: Date;
  readonly budgetedAmount: number;
  readonly clientAmount: number;

  contractorName?: string;

  private readonly totalUsedByOthers: number;

  constructor(
    src: PlannedExpenseMappingRowSrc,
    currentExpenseItemId?: string,
    existingUsage?: number,
    denomination = 2,
  ) {
    super({
      id: new FieldState(src.id),
      mappedAmount: new FieldState(
        new NumberModel(existingUsage ?? 0, denomination),
      ),
    });

    this.description = src.description ?? undefined;
    this.contractorId = src.contractor?.id ?? "";
    this.date = src.date ? new Date(src.date) : undefined;
    this.budgetedAmount = src.budgeted_amount;
    this.clientAmount = src.client_amount;
    this.totalUsedByOthers = src.usedExpenses.reduce<number>((prev, usage) => {
      if (usage.expenseItem.id === currentExpenseItemId) {
        return prev;
      }

      return prev + usage.used_amount;
    }, 0);

    makeObservable(this, {
      contractorName: observable.ref,
      remainingBudgetedAmount: computed,
    });

    this.key = uniqueId("planned-expense-mapping-row-");
    this.contractorName = src.contractor?.legal_name;
  }

  get id() {
    return this.$.id.$;
  }

  get mappedAmount() {
    return this.$.mappedAmount;
  }

  get remainingBudgetedAmount() {
    return Math.max(this.budgetedAmount - this.totalUsedByOthers, 0);
  }

  async serialize() {
    const res = await this.validate();
    if (res.hasError) return null;

    if (res.value.mappedAmount.$.denominatedValue <= 0) {
      return null;
    }

    return {
      budget_item_planned_expense_id: res.value.id.$,
      used_amount: res.value.mappedAmount.$.denominatedValue,
    };
  }
}

export class PlannedExpenseMappingStore {
  @observable.shallow
  rows: PlannedExpenseMappingRowForm[] = [];

  @observable
  candidateCount = 0;

  @observable.ref
  persistedUsages: NonNullable<TAddExpenseItemArgs["planned_expense_usages"]> =
    [];

  readonly modalState = new DisclosureState<PlannedExpenseMappingModalData>({
    onOpen: () => {
      this.rows = [];
      void this.fetchRows();
    },
    onClose: () => {
      this.rows = [];
    },
  });

  private readonly fetcher = new FetchHelper<
    BudgetItemPlannedExpensesForCreateExpenseQuery,
    BudgetItemPlannedExpensesForCreateExpenseQueryVariables
  >(BudgetItemPlannedExpensesForCreateExpenseDocument);

  constructor(
    private readonly budgetItemStore: BudgetItemStore,
    plannedExpenseUsages: NonNullable<
      TAddExpenseItemArgs["planned_expense_usages"]
    > = [],
  ) {
    makeObservable(this);
    this.persistedUsages = plannedExpenseUsages;
  }

  @computed
  get selectedBudgetItemId() {
    return this.budgetItemStore.budgetItemId;
  }

  @computed
  get selectedBudgetItemType() {
    return this.budgetItemStore.form.$.budget_item_type.value;
  }

  @computed
  get selectedExpenseItem() {
    return this.budgetItemStore.selectedExpenseItem;
  }

  @computed
  get requiresMapping() {
    if (
      this.selectedBudgetItemType ===
      ExpenseBudgetItemTypeEnum.ExpenseBudgetItem
    ) {
      return (this.selectedExpenseItem?.stats.planned_expenses ?? 0) > 0;
    }

    if (
      this.selectedBudgetItemType ===
      ExpenseBudgetItemTypeEnum.OurWorkBudgetItem
    ) {
      return this.candidateCount > 0 || this.hasPersistedUsages;
    }

    return false;
  }

  @computed
  get isLoading() {
    return this.fetcher.isLoading.value;
  }

  @computed
  get expenseItemId() {
    return this.budgetItemStore.form.$.id.value;
  }

  @computed
  get visibleRows() {
    const contractorId =
      this.budgetItemStore.expenseStore.form.contractor_id.value;

    return this.rows.filter((row) => {
      if (!contractorId) return true;

      return !row.contractorId || row.contractorId === contractorId;
    });
  }

  @computed
  get totalMappedAmount() {
    return this.rows.reduce((prev, row) => {
      return prev + row.mappedAmount.value.denominatedValue;
    }, 0);
  }

  @computed
  get persistedUsageTotal() {
    return this.persistedUsages.reduce((prev, usage) => {
      return prev + usage.used_amount;
    }, 0);
  }

  @computed
  get rowAmountInWorkspace() {
    return this.budgetItemStore.expenseStore.toWorkspaceNumberModel(
      this.budgetItemStore.form.$.amount_in_currency.value.denominatedValue,
    ).denominatedValue;
  }

  @computed
  get remainingAmount() {
    return this.rowAmountInWorkspace - this.totalMappedAmount;
  }

  @computed
  get isValid() {
    if (!this.requiresMapping) return true;
    return this.remainingAmount === 0;
  }

  @computed
  get hasRows() {
    return this.rows.length > 0;
  }

  @computed
  get hasPersistedUsages() {
    return this.persistedUsages.length > 0;
  }

  @computed
  get hasValidPersistedMappings() {
    if (!this.requiresMapping) return true;
    return this.persistedUsageTotal === this.rowAmountInWorkspace;
  }

  @computed
  get hasVisibleCandidates() {
    return this.visibleRows.length > 0;
  }

  @computed
  get shouldShowTrigger() {
    return this.requiresMapping;
  }

  @computed
  get errorMessage() {
    if (!this.requiresMapping || this.hasValidPersistedMappings) {
      return undefined;
    }

    return t`Planned expenses mapping must equal expense item amount.`;
  }

  @action.bound
  setPersistedUsages(
    usages: NonNullable<TAddExpenseItemArgs["planned_expense_usages"]> = [],
  ) {
    this.persistedUsages = usages;
  }

  @action.bound
  reset() {
    this.rows = [];
    this.candidateCount = 0;
    this.persistedUsages = [];
    this.modalState.onClose();
  }

  @action.bound
  async fetchRows() {
    const budgetItemId = this.selectedBudgetItemId;
    const budgetItemType = this.selectedBudgetItemType;

    if (!budgetItemId) {
      this.rows = [];
      this.candidateCount = 0;
      return;
    }

    // null signal: aborterLink would abort sibling rows that share this
    // operationName, and FetchHelper leaves isLoading on after abort.
    const [data, error] = await this.fetcher.fetch(
      {
        budgetItemId,
        budgetItemType,
      },
      null,
    );

    if (
      budgetItemId !== this.selectedBudgetItemId ||
      budgetItemType !== this.selectedBudgetItemType
    ) {
      return;
    }

    if (error) return;

    this.candidateCount = data.budgetItemPlannedExpenses.length;

    const workspaceDenomination =
      this.budgetItemStore.expenseStore.appStore.workspaceStore.settings
        ?.currency.denomination ?? 2;

    this.rows = data.budgetItemPlannedExpenses.map((row) => {
      const initialUsage = this.persistedUsages.find(
        (usage) => usage.budget_item_planned_expense_id === row.id,
      )?.used_amount;

      return new PlannedExpenseMappingRowForm(
        row,
        this.expenseItemId,
        initialUsage,
        workspaceDenomination,
      );
    });
  }

  @action.bound
  async sync() {
    await this.fetchRows();
  }

  @action.bound
  quickFill(rowId: string) {
    const row = this.rows.find((item) => item.id === rowId);

    if (!row) return;

    const remainingWithoutCurrentRow =
      this.rowAmountInWorkspace -
      this.rows.reduce<number>((prev, item) => {
        if (item.id === rowId) return prev;

        return prev + item.mappedAmount.value.denominatedValue;
      }, 0);

    row.mappedAmount.onChange(
      row.mappedAmount.value.setDenomValue(
        Math.min(row.remainingBudgetedAmount, remainingWithoutCurrentRow),
      ),
    );
  }

  serialize() {
    return this.persistedUsages;
  }

  @action.bound
  async handleSave() {
    const usages = await this.serializeRows();
    if (!usages) return;

    this.modalState.additionalData?.onSave(usages);
    this.modalState.onClose();
  }

  private async serializeRows() {
    const usages: NonNullable<TAddExpenseItemArgs["planned_expense_usages"]> =
      [];

    for (const row of this.rows) {
      const usage = await row.serialize();
      if (!usage) continue;
      usages.push(usage);
    }

    return usages;
  }
}
